import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  ChartOptions,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { Calendar } from 'lucide-react';
import { Device } from '../types';
import analyticsService from '../services/analyticsService';
import {
  AnalyticsContainer,
  DateRangeSelector,
  DateRangeButton,
  DateInputWrapper,
  DateInput,
  ChartsGrid,
  ChartPanel,
  ChartHeader,
  ChartTitle,
  ChartUnit,
  ChartWrapper,
  NoDataMessage
} from '../styles/AnalyticsStyles';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

interface AnalyticsDashboardProps {
  device: Device;
}

type TimeRange = '1h' | '1d' | '1w' | '1m' | 'custom';

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ device }) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('1d');
  const [customStartDate, setCustomStartDate] = useState('2024-10-02');
  const [customEndDate, setCustomEndDate] = useState('2025-10-02');
  const [chartData, setChartData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeRange, customStartDate, customEndDate, device.id]);

  const getDateRange = (): { start: Date; end: Date } => {
    const now = new Date();
    let start = new Date();

    switch (timeRange) {
      case '1h':
        start = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case '1d':
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case '1w':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '1m':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'custom':
        start = new Date(customStartDate);
        return { start, end: new Date(customEndDate) };
    }

    return { start, end: now };
  };

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { start, end } = getDateRange();
      const response = await analyticsService.getDeviceAnalytics(
        device.id.toString(),
        start,
        end
      );

      if (response.dataPoints.length === 0) {
        setError('No data available for the selected time range');
        setChartData(null);
        setLoading(false);
        return;
      }

      // Transform API data based on device type
      const transformedData = transformDataByDeviceType(response.dataPoints);
      setChartData(transformedData);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch analytics data. Please try again.');
      console.error('Error fetching analytics:', err);
      setLoading(false);
    }
  };

  const transformDataByDeviceType = (dataPoints: any[]) => {
    const deviceType = device.deviceType || 'buoy';

    // Helper function to parse values that might be strings
    const parseValue = (val: any): number | null => {
      if (val === null || val === undefined || val === '') return null;
      const parsed = typeof val === 'string' ? parseFloat(val) : val;
      return isNaN(parsed) ? null : parsed;
    };

    // Helper function to filter out null/undefined but keep 0 values
    const filterValid = (point: { x: Date; y: number | null }) => point.y !== null && point.y !== undefined && !isNaN(point.y);

    // Common transformations
    const temperature = dataPoints.map(point => ({
      x: new Date(point.timestamp),
      y: parseValue(point.temperature)
    })).filter(filterValid);

    // Device-type-specific transformations
    if (deviceType === 'buoy') {
      return {
        // Wave Measurements
        significantWaveHeight: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.significant_wave_height)
        })).filter(filterValid),

        maximumWaveHeight: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.maximum_wave_height)
        })).filter(filterValid),

        peakWavePeriod: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.peak_wave_period)
        })).filter(filterValid),

        meanWavePeriod: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.mean_wave_period)
        })).filter(filterValid),

        peakWaveDirection: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.peak_wave_direction)
        })).filter(filterValid),

        averageWaveHeight: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.average_wave_height)
        })).filter(filterValid),

        // Temperature Measurements
        temperature,

        internalSeaTemperature: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.internal_sea_temperature)
        })).filter(filterValid),

        externalSeaTemperature: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.external_sea_temperature)
        })).filter(filterValid),

        boardTemperature: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.board_temperature)
        })).filter(filterValid),

        housingPeripheralEdgeTemperature: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.housing_peripheral_edge_temperature)
        })).filter(filterValid),

        spareTemperature: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.spare_temperature)
        })).filter(filterValid),

        batteryTemperature: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.battery_temperature)
        })).filter(filterValid),

        temperatureTroll: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.temperature_troll)
        })).filter(filterValid),

        // RDO (Dissolved Oxygen) Measurements
        rdoConcentration: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.rdo_concentration)
        })).filter(filterValid),

        rdoSaturation: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.rdo_saturation)
        })).filter(filterValid),

        // Power System
        batteryVoltage: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.battery_voltage)
        })).filter(filterValid),

        rtcBatteryVoltage: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.rtc_battery_voltage)
        })).filter(filterValid),

        processorRegulatedVoltage: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.microchip_regulated_voltage)
        })).filter(filterValid),

        solarPanelVoltage: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.solar_panel_voltage)
        })).filter(filterValid),

        solarPanelCurrent: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.solar_panel_current)
        })).filter(filterValid),

        batteryCapacity: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.battery_capacity)
        })).filter(filterValid),

        batteryCurrent: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.battery_current)
        })).filter(filterValid),

        batteryFill: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.battery_fill)
        })).filter(filterValid),

        batteryRelativeFill: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.battery_relative_fill)
        })).filter(filterValid),

        // GPS / Location
        latitude: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.latitude)
        })).filter(filterValid),

        longitude: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.longitude)
        })).filter(filterValid),

        // Communication
        signalStrength: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.signal_strength)
        })).filter(filterValid),

        // Software & System
        softwareVersion: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.software_version)
        })).filter(filterValid),

        // Spectrum Analysis
        spectrumSignificantWaveHeight: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.spectrum_significant_wave_height)
        })).filter(filterValid),

        spectrumMaximumWaveHeight: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.spectrum_maximum_wave_height)
        })).filter(filterValid),

        spectrumPeakWavePeriod: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.spectrum_peak_wave_period)
        })).filter(filterValid),

        spectrumMeanWavePeriod: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.spectrum_mean_wave_period)
        })).filter(filterValid),

        spectrumPeakWaveDirection: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.spectrum_peak_wave_direction)
        })).filter(filterValid),

        spectrumAverageWaveHeight: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.spectrum_average_wave_height)
        })).filter(filterValid),

        // Spectrum Coefficients
        a1SpectrumCoefficient: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.a1_spectrum_coefficient)
        })).filter(filterValid),

        b1SpectrumCoefficient: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.b1_spectrum_coefficient)
        })).filter(filterValid),

        a2SpectrumCoefficient: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.a2_spectrum_coefficient)
        })).filter(filterValid),

        b2SpectrumCoefficient: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.b2_spectrum_coefficient)
        })).filter(filterValid),

        periodHighestWave: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.period_highest_wave)
        })).filter(filterValid),

        // NOTE: Removed placeholder metrics (Analog 0-2, DIO State, Processor ID, In-Situ RDO Oxygen metrics)
        // as they are not yet implemented in the buoy messages
      };
    }

    if (deviceType === 'netatmo_weather') {
      return {
        temperature,
        humidity: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.humidity)
        })).filter(filterValid),

        co2: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.co2)
        })).filter(filterValid),

        noise: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.noise)
        })).filter(filterValid),

        pressure: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.pressure)
        })).filter(filterValid),

        absolutePressure: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.absolute_pressure)
        })).filter(filterValid),
      };
    }

    if (deviceType === 'netatmo_outdoor' || deviceType === 'netatmo_indoor') {
      return {
        temperature,
        humidity: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.humidity)
        })).filter(filterValid),

        batteryPercent: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.battery_percent)
        })).filter(filterValid),
      };
    }

    if (deviceType === 'netatmo_rain') {
      return {
        rain: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.rain)
        })).filter(filterValid),

        rain1h: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.sum_rain_1)
        })).filter(filterValid),

        rain24h: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.sum_rain_24)
        })).filter(filterValid),

        batteryPercent: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.battery_percent)
        })).filter(filterValid),
      };
    }

    if (deviceType === 'netatmo_wind') {
      return {
        windStrength: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.wind_strength)
        })).filter(filterValid),

        windAngle: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.wind_angle)
        })).filter(filterValid),

        gustStrength: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.gust_strength)
        })).filter(filterValid),

        gustAngle: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.gust_angle)
        })).filter(filterValid),

        batteryPercent: dataPoints.map(point => ({
          x: new Date(point.timestamp),
          y: parseValue(point.battery_percent)
        })).filter(filterValid),
      };
    }

    // Default: return temperature only
    return { temperature };
  };

  const renderChartsByDeviceType = () => {
    if (!chartData) return null;

    const deviceType = device.deviceType || 'buoy';

    if (deviceType === 'buoy') {
      return renderBuoyCharts();
    }

    if (deviceType === 'netatmo_weather') {
      return renderNetatmoWeatherCharts();
    }

    if (deviceType === 'netatmo_outdoor' || deviceType === 'netatmo_indoor') {
      return renderNetatmoIndoorOutdoorCharts();
    }

    if (deviceType === 'netatmo_rain') {
      return renderNetatmoRainCharts();
    }

    if (deviceType === 'netatmo_wind') {
      return renderNetatmoWindCharts();
    }

    return null;
  };

  const renderNetatmoWeatherCharts = () => (
    <>
      {renderChart('Temperature', chartData.temperature, '°C', '#ef4444')}
      {renderChart('Humidity', chartData.humidity, '%', '#3b82f6')}
      {renderChart('CO₂ Level', chartData.co2, 'ppm', '#f59e0b')}
      {renderChart('Noise Level', chartData.noise, 'dB', '#8b5cf6')}
      {renderChart('Atmospheric Pressure', chartData.pressure, 'mbar', '#10b981')}
    </>
  );

  const renderNetatmoIndoorOutdoorCharts = () => (
    <>
      {renderChart('Temperature', chartData.temperature, '°C', '#ef4444')}
      {renderChart('Humidity', chartData.humidity, '%', '#3b82f6')}
      {renderChart('Battery Level', chartData.batteryPercent, '%', '#10b981')}
    </>
  );

  const renderNetatmoRainCharts = () => (
    <>
      {renderChart('Rain Rate', chartData.rain, 'mm/h', '#3b82f6')}
      {renderChart('Rain Last Hour', chartData.rain1h, 'mm', '#0ea5e9')}
      {renderChart('Rain Last 24 Hours', chartData.rain24h, 'mm', '#06b6d4')}
      {renderChart('Battery Level', chartData.batteryPercent, '%', '#10b981')}
    </>
  );

  const renderNetatmoWindCharts = () => (
    <>
      {renderChart('Wind Speed', chartData.windStrength, 'km/h', '#14b8a6')}
      {renderChart('Wind Direction', chartData.windAngle, '°', '#8b5cf6')}
      {renderChart('Gust Speed', chartData.gustStrength, 'km/h', '#f59e0b')}
      {renderChart('Battery Level', chartData.batteryPercent, '%', '#10b981')}
    </>
  );

  const renderBuoyCharts = () => (
    <>
      {/* Wave Measurements */}
      {renderChart('Significant Wave Height', chartData.significantWaveHeight, 'M', '#3b82f6')}
      {renderChart('Maximum Wave Height', chartData.maximumWaveHeight, 'M', '#ef4444')}
      {renderChart('Peak Wave Period', chartData.peakWavePeriod, 'S', '#06b6d4')}
      {renderChart('Mean Wave Period', chartData.meanWavePeriod, 'S', '#0ea5e9')}
      {renderChart('Peak Frequency Wave Direction', chartData.peakWaveDirection, '°', '#8b5cf6')}
      {renderChart('Average Wave Height', chartData.averageWaveHeight, 'M', '#14b8a6')}

      {/* Temperature Measurements */}
      {renderChart('Temperature', chartData.temperature, '°C', '#f59e0b')}

      {/* GPS / Location */}
      {renderChart('GPS Latitude', chartData.latitude, 'Decimal Degree', '#ffbb33')}
      {renderChart('GPS Longitude', chartData.longitude, 'Decimal Degree', '#ff4444')}

      {/* Communication */}
      {renderChart('Iridium Signal Strength', chartData.signalStrength, '', '#8b5cf6')}

      {/* Power System */}
      {renderChart('Processor Regulated Voltage', chartData.processorRegulatedVoltage, 'V', '#6ee7b7')}
      {renderChart('RTC Battery Voltage', chartData.rtcBatteryVoltage, 'V', '#4ade80')}

      {/* RDO Measurements */}
      {renderChart('RDO Concentration', chartData.rdoConcentration, 'mg/L', '#00bfff')}
      {renderChart('RDO Saturation', chartData.rdoSaturation, '%', '#0ea5e9')}
      {renderChart('Temperature Troll', chartData.temperatureTroll, '°C', '#fbbf24')}

      {/* Additional Temperature Measurements */}
      {renderChart('Solar Housing Peripheral Edge Temperature (ID 0) (LOW RES)', chartData.housingPeripheralEdgeTemperature, '°C', '#fcd34d')}
      {renderChart('Sea Temperature (Potted Core) (ID 1) (HIGH RES)', chartData.internalSeaTemperature, '°C', '#ef4444')}
      {renderChart('External Sea Temperature (ID 2) (HIGH RES)', chartData.externalSeaTemperature, '°C', '#f97316')}
      {renderChart('Spare Temperature (ID3) (HIGH RES)', chartData.spareTemperature, '°C', '#fb923c')}
      {renderChart('AquaSense 100 Board Temperature (ID 4) (LOW RES)', chartData.boardTemperature, '°C', '#fdba74')}

      {/* APT Solar PCB Power System */}
      {renderChart('APT Solar PCB Panel Voltage', chartData.solarPanelVoltage, 'mV', '#fbbf24')}
      {renderChart('APT Solar PCB Panel Current', chartData.solarPanelCurrent, 'mA', '#fcd34d')}
      {renderChart('APT Solar PCB Battery Voltage', chartData.batteryVoltage, 'mV', '#10b981')}
      {renderChart('APT Solar PCB Battery Current', chartData.batteryCurrent, 'mA', '#84cc16')}
      {renderChart('APT Solar PCB Battery Capacity', chartData.batteryCapacity, 'mAh', '#a3e635')}
      {renderChart('APT Solar PCB Battery Fill', chartData.batteryFill, 'mAh', '#22c55e')}
      {renderChart('APT Solar PCB Battery Rel. Fill', chartData.batteryRelativeFill, 'mAh', '#16a34a')}
      {renderChart('APT Solar PCB Battery Temperature', chartData.batteryTemperature, '°C', '#34d399')}

      {/* Spectrum Analysis */}
      {renderChart('Spectrum - Significant Wave Height', chartData.spectrumSignificantWaveHeight, 'm', '#6366f1')}
      {renderChart('Spectrum - Maximum Wave Height', chartData.spectrumMaximumWaveHeight, 'm', '#dc2626')}
      {renderChart('Spectrum - Peak Wave Period', chartData.spectrumPeakWavePeriod, 's', '#0891b2')}
      {renderChart('Spectrum - Mean Wave Period', chartData.spectrumMeanWavePeriod, 's', '#0284c7')}
      {renderChart('Spectrum - Peak Wave Direction', chartData.spectrumPeakWaveDirection, '°', '#7c3aed')}
      {renderChart('Spectrum - Wave Height Average', chartData.spectrumAverageWaveHeight, 'm', '#059669')}
    </>
  );

  const renderChart = (title: string, data: any[], unit: string, color: string) => {
    if (!data || data.length === 0) {
      return (
        <ChartPanel key={title}>
          <ChartHeader>
            <ChartTitle>{title}</ChartTitle>
            <ChartUnit>{unit}</ChartUnit>
          </ChartHeader>
          <ChartWrapper>
            <NoDataMessage>No data available</NoDataMessage>
          </ChartWrapper>
        </ChartPanel>
      );
    }

    const chartDataset = {
      labels: data.map(d => d.x),
      datasets: [
        {
          label: title,
          data: data,
          borderColor: color,
          backgroundColor: `${color}20`,
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          tension: 0.4,
          fill: true,
        },
      ],
    };

    return (
      <ChartPanel key={title}>
        <ChartHeader>
          <ChartTitle>{title}</ChartTitle>
          <ChartUnit>{unit}</ChartUnit>
        </ChartHeader>
        <ChartWrapper>
          <Line data={chartDataset} options={getChartOptions()} />
        </ChartWrapper>
      </ChartPanel>
    );
  };

  const getChartOptions = (): ChartOptions<'line'> => {
    // Determine the appropriate time unit and format based on selected range
    let timeUnit: 'minute' | 'hour' | 'day' | 'week' | 'month' = 'hour';
    let tooltipFormat = 'HH:mm';

    // Get the date range for x-axis bounds
    const { start, end } = getDateRange();

    switch (timeRange) {
      case '1h':
        timeUnit = 'minute';
        tooltipFormat = 'MMM d, HH:mm';
        break;
      case '1d':
        timeUnit = 'hour';
        tooltipFormat = 'MMM d, HH:mm';
        break;
      case '1w':
        timeUnit = 'day';
        tooltipFormat = 'MMM d, yyyy';
        break;
      case '1m':
        timeUnit = 'day';
        tooltipFormat = 'MMM d, yyyy';
        break;
      case 'custom':
        // For custom, calculate the range and decide
        const customStart = new Date(customStartDate);
        const customEnd = new Date(customEndDate);
        const daysDiff = (customEnd.getTime() - customStart.getTime()) / (1000 * 60 * 60 * 24);

        if (daysDiff <= 2) {
          timeUnit = 'hour';
          tooltipFormat = 'MMM d, HH:mm';
        } else if (daysDiff <= 31) {
          timeUnit = 'day';
          tooltipFormat = 'MMM d, yyyy';
        } else if (daysDiff <= 90) {
          timeUnit = 'week';
          tooltipFormat = 'MMM d, yyyy';
        } else {
          timeUnit = 'month';
          tooltipFormat = 'MMM yyyy';
        }
        break;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#1a1a1a',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#404040',
          borderWidth: 1,
          padding: 12,
          displayColors: false,
          callbacks: {
            title: (context) => {
              const date = new Date(context[0].parsed.x);
              // Format based on time range
              if (timeUnit === 'minute' || timeUnit === 'hour') {
                return date.toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                });
              } else if (timeUnit === 'day' || timeUnit === 'week') {
                return date.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                });
              } else {
                return date.toLocaleDateString('en-US', {
                  month: 'short',
                  year: 'numeric'
                });
              }
            }
          }
        }
      },
      scales: {
        x: {
          type: 'time' as const,
          time: {
            unit: timeUnit,
            displayFormats: {
              minute: 'HH:mm',
              hour: 'HH:mm',
              day: 'MMM d',
              week: 'MMM d',
              month: 'MMM yyyy'
            },
            tooltipFormat: tooltipFormat
          },
          min: start.toISOString(),
          max: end.toISOString(),
          grid: {
            color: '#2a2a2a'
          },
          ticks: {
            color: '#888',
            maxRotation: 0,
            autoSkipPadding: 20,
            source: 'auto',
            autoSkip: true
          }
        },
        y: {
          grid: {
            color: '#2a2a2a'
          },
          ticks: {
            color: '#888'
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    };
  };

  if (loading) {
    return (
      <AnalyticsContainer>
        <NoDataMessage>Loading analytics data...</NoDataMessage>
      </AnalyticsContainer>
    );
  }

  if (error) {
    return (
      <AnalyticsContainer>
        <DateRangeSelector>
          <div style={{ display: 'flex', gap: '8px' }}>
            <DateRangeButton
              active={timeRange === '1h'}
              onClick={() => setTimeRange('1h')}
            >
              1 Hour
            </DateRangeButton>
            <DateRangeButton
              active={timeRange === '1d'}
              onClick={() => setTimeRange('1d')}
            >
              1 Day
            </DateRangeButton>
            <DateRangeButton
              active={timeRange === '1w'}
              onClick={() => setTimeRange('1w')}
            >
              1 Week
            </DateRangeButton>
            <DateRangeButton
              active={timeRange === '1m'}
              onClick={() => setTimeRange('1m')}
            >
              1 Month
            </DateRangeButton>
            <DateRangeButton
              active={timeRange === 'custom'}
              onClick={() => setTimeRange('custom')}
            >
              Custom
            </DateRangeButton>
          </div>

          {timeRange === 'custom' && (
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <DateInputWrapper>
                <Calendar size={14} />
                <span style={{ fontSize: '12px', color: '#888' }}>FROM</span>
                <DateInput
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </DateInputWrapper>
              <DateInputWrapper>
                <Calendar size={14} />
                <span style={{ fontSize: '12px', color: '#888' }}>TO</span>
                <DateInput
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </DateInputWrapper>
            </div>
          )}
        </DateRangeSelector>
        <NoDataMessage>{error}</NoDataMessage>
      </AnalyticsContainer>
    );
  }

  if (!chartData) {
    return (
      <AnalyticsContainer>
        <NoDataMessage>No chart data available</NoDataMessage>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <DateRangeSelector>
        <div style={{ display: 'flex', gap: '8px' }}>
          <DateRangeButton
            active={timeRange === '1h'}
            onClick={() => setTimeRange('1h')}
          >
            1 Hour
          </DateRangeButton>
          <DateRangeButton
            active={timeRange === '1d'}
            onClick={() => setTimeRange('1d')}
          >
            1 Day
          </DateRangeButton>
          <DateRangeButton
            active={timeRange === '1w'}
            onClick={() => setTimeRange('1w')}
          >
            1 Week
          </DateRangeButton>
          <DateRangeButton
            active={timeRange === '1m'}
            onClick={() => setTimeRange('1m')}
          >
            1 Month
          </DateRangeButton>
          <DateRangeButton
            active={timeRange === 'custom'}
            onClick={() => setTimeRange('custom')}
          >
            Custom
          </DateRangeButton>
        </div>

        {timeRange === 'custom' && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <DateInputWrapper>
              <Calendar size={14} />
              <span style={{ fontSize: '12px', color: '#888' }}>FROM</span>
              <DateInput
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
            </DateInputWrapper>
            <DateInputWrapper>
              <Calendar size={14} />
              <span style={{ fontSize: '12px', color: '#888' }}>TO</span>
              <DateInput
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </DateInputWrapper>
          </div>
        )}
      </DateRangeSelector>

      <ChartsGrid>
        {renderChartsByDeviceType()}
      </ChartsGrid>
    </AnalyticsContainer>
  );
};

export default AnalyticsDashboard;

